import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useGetPaymentMethods } from "src/api/generic";
import { LoadingButton } from "@mui/lab";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import {
  useAddInvoiceClientBalancePaymentMutation,
  useAddInvoicePaymentMutation,
  useGetClientUnpaidInvoices,
} from "src/api/sales/invoices";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";

const schema = yup.object().shape({
  type: yup
    .string()
    .required("Required")
    .oneOf(["payment", "clientBalance"] as const),
  amount: yup
    .string()
    .required("Required")
    .test(
      "min-value",
      "Must be greater than 0",
      (value) => parseFloat(value) > 0,
    ),
  payment_method_id: yup.string().when("type", {
    is: (value: string) => value === "payment",
    then: (value) => value.required("Required"),
  }),
  clientInvoiceId: yup.string().when("type", {
    is: (value: string) => value === "clientBalance",
    then: (value) => value.required("Required"),
  }),
  date: yup.date().required("Required"),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

interface IProps {
  open: boolean;
  handleClose: () => void;
  refetch: Function;
  row: any;
}

const AddInvoicePaymentPopup = (props: IProps) => {
  const { open, handleClose, refetch, row } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [files, setFiles] = useState<any>([]);
  const { t } = useTranslation("operationCost");
  const { t: purchasingT } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const { t: salesT } = useTranslation("addSales");

  //React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      type: "payment",
    },
  });

  //APIS
  const { data: paymentsData } = useGetPaymentMethods();
  const { mutate, status, isPending, error } = useAddInvoicePaymentMutation();
  const {
    mutate: mutateBalance,
    status: statusBalance,
    isPending: isPendingBalance,
    error: errorBalance,
  } = useAddInvoiceClientBalancePaymentMutation();
  const { data: clientInvoices } = useGetClientUnpaidInvoices(row.client_id);

  const onClose = () => {
    handleClose();
    reset();
  };

  const selectedClientInvoice = (clientInvoices?.data ?? []).find(
    (owner: any) => owner.id === Number(watch("clientInvoiceId")),
  );

  const clientBalance = selectedClientInvoice?.total_remaining ?? 0;

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { date, payment_method_id, type, clientInvoiceId } = data;

    const dataToSend: any = {
      sale_invoice_id: row.id,
      amount: +data.amount,
      branch_id: row.branch_id,
      date: moment(date).format("YYYY-MM-DD"),
      ...(type === "payment" && {
        payment_method_id: payment_method_id,
      }),
      ...(type === "clientBalance" && {
        balance_sale_invoice_id: clientInvoiceId,
      }),
    };
    const formData = new FormData();
    // append data
    for (const key in dataToSend) {
      formData.append(key, dataToSend[key]);
    }
    // append files
    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }

    if (type === "clientBalance") {
      mutateBalance(formData);
    } else {
      mutate(formData);
    }
  };

  //Add payment success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Add client balance payment success and error handling
  useEffect(() => {
    if (statusBalance === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (statusBalance === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorBalance?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusBalance]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100%", sm: 547 },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {purchasingT("payment")}
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Stack direction={"row"} alignItems={"center"} gap={3}>
              <Typography>{purchasingT("paymentType")}</Typography>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  width: "fit-content",
                  ml: { xs: "auto", sm: "unset" },
                }}
              >
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      key={field.value}
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                    >
                      {["payment", "clientBalance"].map((item) => (
                        <FormControlLabel
                          value={item}
                          control={
                            <Radio
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                              size="small"
                            />
                          }
                          label={
                            item === "clientBalance"
                              ? `${salesT("clientBalance")} (${clientBalance})`
                              : purchasingT(item)
                          }
                          sx={{ color: "#98A2B3" }}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Stack>
            {watch("type") === "payment" ? (
              <Stack spacing={2}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.payment_method_id}
                >
                  <InputLabel id="demo-simple-select-label">
                    {t("paymentMethod")}
                  </InputLabel>
                  <Controller
                    name="payment_method_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label={t("paymentMethod")}
                      >
                        {paymentsData?.data
                          ?.filter((pay) => pay.status)
                          .map(({ id, name }) => {
                            return (
                              <MenuItem key={id} value={`${id}`}>
                                {name}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {errors.payment_method_id?.message}
                  </FormHelperText>
                </FormControl>
                {/* date */}
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.date,
                          helperText: t(`${errors.date?.message ?? ""}`),
                          size: "small",
                        },
                      }}
                      format={"dd/MM/yyyy"}
                      label={t("paymentDate")}
                    />
                  )}
                />
              </Stack>
            ) : null}

            {watch("type") === "clientBalance" ? (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.clientInvoiceId}
              >
                <InputLabel id="demo-simple-select-label">
                  {salesT("client")}
                </InputLabel>
                <Controller
                  name="clientInvoiceId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      key={field.value}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={purchasingT("event")}
                    >
                      {clientInvoices?.data.map(({ id, name }: any) => {
                        return (
                          <MenuItem key={id} value={`${id}`}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                />
                <FormHelperText id="my-helper-text">
                  {errors.clientInvoiceId?.message}
                </FormHelperText>
              </FormControl>
            ) : null}

            {/* Amount */}
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={t("amount")}
                    size="small"
                    {...field}
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                  />
                )}
              />
            </Stack>
          </Stack>
          {/* files */}
          <Box width={"100%"} mt={2}>
            <FilePondUploader
              onUpload={(e) => setFiles(e)}
              maxFiles={3}
              maxFileSize={3}
            />
          </Box>
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={onClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              fullWidth
              type="submit"
              loading={isPending || isPendingBalance}
            >
              {generalT("confirm")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default AddInvoicePaymentPopup;
